'use client';

import { configureTelemetry, type WorkflowTelemetryEvent } from './telemetry';

const STORAGE_KEY = 'kontora_telemetry_buffer_v1';
const MAX_EVENTS = 500;

export function getStoredEvents(): WorkflowTelemetryEvent[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as WorkflowTelemetryEvent[];
  } catch {
    return [];
  }
}

export function clearStoredEvents(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

export function configureBufferingSink(): void {
  configureTelemetry({
    sink: (event) => {
      if (typeof window === 'undefined') return;
      try {
        const events = getStoredEvents();
        events.push(event);
        if (events.length > MAX_EVENTS) events.shift();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[telemetry] Buffer-Persistierung fehlgeschlagen:', err);
      }
    },
  });
}

export interface WorkflowAnalytics {
  workflowCode: string;
  starts: number;
  completions: number;
  cancellations: number;
  skips: number;
  completionRate: number;
  averageStepsCompleted: number;
}

export function computeAnalytics(events: WorkflowTelemetryEvent[]): WorkflowAnalytics[] {
  const map = new Map<string, WorkflowAnalytics>();
  const stepsByWorkflow = new Map<string, number[]>();

  for (const event of events) {
    let analytics = map.get(event.workflowCode);
    if (!analytics) {
      analytics = {
        workflowCode: event.workflowCode,
        starts: 0,
        completions: 0,
        cancellations: 0,
        skips: 0,
        completionRate: 0,
        averageStepsCompleted: 0,
      };
      map.set(event.workflowCode, analytics);
    }

    if (event.type === 'workflow_start') {
      analytics.starts += 1;
      stepsByWorkflow.set(event.workflowCode, []);
    } else if (event.type === 'workflow_step_advance') {
      const steps = stepsByWorkflow.get(event.workflowCode) ?? [];
      steps.push(event.toStep);
      stepsByWorkflow.set(event.workflowCode, steps);
    } else if (event.type === 'workflow_complete') {
      analytics.completions += 1;
    } else if (event.type === 'workflow_cancel') {
      analytics.cancellations += 1;
    } else if (event.type === 'workflow_skip_to_manual') {
      analytics.skips += 1;
    }
  }

  for (const analytics of map.values()) {
    const totalClosed = analytics.completions + analytics.cancellations + analytics.skips;
    analytics.completionRate = totalClosed > 0 ? analytics.completions / totalClosed : 0;
    const steps = stepsByWorkflow.get(analytics.workflowCode) ?? [];
    if (steps.length > 0) {
      analytics.averageStepsCompleted = steps.reduce((a, b) => a + b, 0) / steps.length;
    }
  }

  return Array.from(map.values()).sort((a, b) => b.starts - a.starts);
}